import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import CategoryFilter from '../../components/ui/CategoryFilter';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const Account = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, signOut } = useAuth();
  const [profile, setProfile] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/sign-in');
      return;
    }

    if (user) {
      fetchUserData();
    }
  }, [user, authLoading, navigate]);

  const fetchUserData = async () => {
    try {
      const { data: profileData, error: profileError } = await supabase?.from('user_profiles')?.select('*')?.eq('id', user?.id)?.single();

      if (profileError) throw profileError;
      setProfile(profileData);

      const { data: purchasesData, error: purchasesError } = await supabase?.from('purchases')?.select(`
          *,
          ebook:ebooks(*)
        `)?.eq('user_id', user?.id)?.eq('payment_status', 'succeeded')?.order('created_at', { ascending: false });

      if (purchasesError) throw purchasesError;
      setPurchases(purchasesData || []);
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/e-book-catalog');
  };

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <CategoryFilter />
        <main className="pt-32 pb-12">
          <div className="max-w-4xl mx-auto px-4">
            <div className="flex items-center justify-center py-12">
              <Icon name="Loader2" size={32} className="animate-spin text-primary" />
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>My Account - StrategyShelf</title>
        <meta name="description" content="Manage your StrategyShelf account and view your purchase history." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <CategoryFilter />

        <main className="pt-32 pb-12 md:pb-16 lg:pb-20">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h1 className="text-3xl md:text-4xl font-heading font-semibold text-foreground mb-6 md:mb-8">
              My Account
            </h1>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-1">
                <div className="bg-card rounded-lg border border-border p-6">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                      <Icon name="User" size={32} className="text-primary" />
                    </div>
                    <div>
                      <h2 className="text-lg font-heading font-semibold text-foreground">
                        {profile?.full_name || 'User'}
                      </h2>
                      <p className="text-sm text-muted-foreground">{profile?.email}</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Button
                      variant="outline"
                      fullWidth
                      iconName="Download"
                      iconPosition="left"
                      onClick={() => navigate('/downloads')}
                    >
                      My Downloads
                    </Button>

                    <Button
                      variant="outline"
                      fullWidth
                      iconName="LogOut"
                      iconPosition="left"
                      onClick={handleSignOut}
                    >
                      Sign Out
                    </Button>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-2">
                <div className="bg-card rounded-lg border border-border p-6">
                  <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                    Purchase History
                  </h2>

                  {purchases?.length === 0 ? (
                    <div className="text-center py-12">
                      <Icon name="ShoppingBag" size={48} className="text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground mb-4">No purchases yet</p>
                      <Button
                        variant="default"
                        onClick={() => navigate('/e-book-catalog')}
                      >
                        Browse eBooks
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {purchases?.map((purchase) => (
                        <div
                          key={purchase?.id}
                          className="flex items-start gap-4 p-4 border border-border rounded-lg hover:bg-muted/50 transition-smooth"
                        >
                          <img
                            src={purchase?.ebook?.cover_image}
                            alt={purchase?.ebook?.cover_image_alt}
                            className="w-16 h-24 object-cover rounded-md"
                          />
                          <div className="flex-1">
                            <h3 className="font-medium text-foreground mb-1">
                              {purchase?.ebook?.title}
                            </h3>
                            <p className="text-sm text-muted-foreground mb-2">
                              by {purchase?.ebook?.author}
                            </p>
                            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                              <span>
                                {new Date(purchase?.created_at)?.toLocaleDateString()}
                              </span>
                              <span className="font-medium text-success">
                                ${purchase?.amount?.toFixed(2)}
                              </span>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            iconName="Download"
                            onClick={() => navigate('/downloads')}
                          >
                            Download
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default Account;